import { 
  collection, 
  doc, 
  getDocs, 
  getDoc, 
  addDoc, 
  updateDoc, 
  deleteDoc, 
  query, 
  where, 
  orderBy, 
  limit, 
  writeBatch,
  DocumentReference
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Episode } from '../types/types';

const COLLECTION_NAME = 'episodes';

/**
 * Service for managing episodes in Firestore
 */
export class EpisodeService {
  /**
   * Get all episodes ordered by date (newest first)
   */
  static async getAllEpisodes(): Promise<Episode[]> {
    try {
      const episodesQuery = query(
        collection(db, COLLECTION_NAME),
        orderBy('date', 'desc')
      );
      
      const snapshot = await getDocs(episodesQuery);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data() as Omit<Episode, 'id'>
      }));
    } catch (error) {
      console.error('Error getting episodes:', error);
      return [];
    }
  }
  
  /**
   * Get the most recent episode
   */
  static async getRecentEpisode(): Promise<Episode | null> {
    try {
      const recentQuery = query(
        collection(db, COLLECTION_NAME),
        where('isRecent', '==', true),
        limit(1)
      );
      
      const snapshot = await getDocs(recentQuery);
      
      if (snapshot.empty) {
        // If no episode is marked as recent, get the latest one by date
        const latestQuery = query(
          collection(db, COLLECTION_NAME),
          orderBy('date', 'desc'),
          limit(1)
        );
        
        const latestSnapshot = await getDocs(latestQuery);
        
        if (latestSnapshot.empty) {
          return null;
        }
        
        return {
          id: latestSnapshot.docs[0].id,
          ...latestSnapshot.docs[0].data() as Omit<Episode, 'id'>
        };
      }
      
      return {
        id: snapshot.docs[0].id,
        ...snapshot.docs[0].data() as Omit<Episode, 'id'>
      };
    } catch (error) {
      console.error('Error getting recent episode:', error);
      return null;
    }
  }
  
  /**
   * Add a new episode
   */
  static async addEpisode(episode: Omit<Episode, 'id' | 'createdAt' | 'updatedAt'>): Promise<Episode | null> {
    try {
      const now = new Date();
      
      // Create the episode data with metadata
      const episodeData = {
        ...episode,
        createdAt: now,
        updatedAt: now
      };
      
      // Add to Firestore
      const docRef = await addDoc(collection(db, COLLECTION_NAME), episodeData);
      
      // Return the complete episode object
      return {
        id: docRef.id,
        ...episodeData
      };
    } catch (error) {
      console.error('Error adding episode:', error);
      return null;
    }
  }
  
  /**
   * Update an existing episode
   */
  static async updateEpisode(id: string, data: Partial<Episode>): Promise<boolean> {
    try {
      const episodeRef = doc(db, COLLECTION_NAME, id);
      
      await updateDoc(episodeRef, {
        ...data,
        updatedAt: new Date()
      });
      
      return true;
    } catch (error) {
      console.error('Error updating episode:', error);
      return false;
    }
  }
  
  /**
   * Set an episode as the recent episode
   */
  static async setRecentEpisode(id: string): Promise<boolean> {
    try {
      // First, unset all recent episodes
      const episodesQuery = query(
        collection(db, COLLECTION_NAME),
        where('isRecent', '==', true)
      );
      
      const snapshot = await getDocs(episodesQuery);
      
      const updatePromises = snapshot.docs.map(doc => 
        updateDoc(doc.ref, { 
          isRecent: false,
          updatedAt: new Date()
        })
      );
      
      await Promise.all(updatePromises);
      
      // Then set the new recent episode
      const episodeRef = doc(db, COLLECTION_NAME, id);
      await updateDoc(episodeRef, { 
        isRecent: true,
        updatedAt: new Date()
      });
      
      return true;
    } catch (error) {
      console.error('Error setting recent episode:', error);
      return false;
    }
  }
  
  /**
   * Delete an episode
   */
  static async deleteEpisode(id: string): Promise<boolean> {
    try {
      // Check if this is the recent episode
      const episodeRef = doc(db, COLLECTION_NAME, id);
      const episodeDoc = await getDoc(episodeRef);
      
      if (episodeDoc.exists() && episodeDoc.data().isRecent) {
        // Get the next most recent episode by date to set as recent
        const nextRecentQuery = query(
          collection(db, COLLECTION_NAME),
          where('id', '!=', id),
          orderBy('date', 'desc'),
          limit(1)
        );
        
        const nextRecentSnapshot = await getDocs(nextRecentQuery);
        
        if (!nextRecentSnapshot.empty) {
          // Set the next episode as recent
          await updateDoc(nextRecentSnapshot.docs[0].ref, {
            isRecent: true,
            updatedAt: new Date()
          });
        }
      }
      
      // Delete the episode
      await deleteDoc(episodeRef);
      return true;
    } catch (error) {
      console.error('Error deleting episode:', error);
      return false;
    }
  }
  
  /**
   * Delete multiple episodes
   */
  static async deleteMultipleEpisodes(ids: string[]): Promise<boolean> {
    try {
      let needToSetNewRecent = false;
      
      for (const id of ids) {
        const episodeRef = doc(db, COLLECTION_NAME, id);
        const episodeDoc = await getDoc(episodeRef);
        
        if (episodeDoc.exists() && episodeDoc.data().isRecent) {
          needToSetNewRecent = true;
          break;
        }
      }
      
      // If we're deleting the recent episode, find a new one
      if (needToSetNewRecent) {
        // Get all episodes not in the deletion list
        const allEpisodesQuery = query(
          collection(db, COLLECTION_NAME),
          orderBy('date', 'desc')
        );
        
        const allEpisodesSnapshot = await getDocs(allEpisodesQuery);
        
        // Find the first episode that's not in the deletion list
        let newRecentEpisodeRef: DocumentReference | null = null;
        
        for (const doc of allEpisodesSnapshot.docs) {
          if (!ids.includes(doc.id)) {
            newRecentEpisodeRef = doc.ref;
            break;
          }
        }
        
        // Set the new episode as recent
        if (newRecentEpisodeRef) {
          await updateDoc(newRecentEpisodeRef, {
            isRecent: true,
            updatedAt: new Date()
          });
        }
      }
      
      // Use a batched write to delete all episodes (more efficient for multiple operations)
      const batch = writeBatch(db);
      ids.forEach(id => {
        const episodeRef = doc(db, COLLECTION_NAME, id);
        batch.delete(episodeRef);
      });
      
      await batch.commit();
      
      return true;
    } catch (error) {
      console.error('Error deleting multiple episodes:', error);
      return false;
    }
  }
  
  /**
   * Initialize with sample data if collection is empty
   */
  static async initializeWithSampleData(episodes: Omit<Episode, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<void> {
    try {
      const snapshot = await getDocs(collection(db, COLLECTION_NAME));
      
      if (snapshot.empty) {
        
        const now = new Date();
        const batch = episodes.map(async (episode, index) => {
          await addDoc(collection(db, COLLECTION_NAME), {
            ...episode,
            isRecent: index === 0, // First episode is the recent one
            createdAt: now,
            updatedAt: now
          });
        });
        
        await Promise.all(batch);
      }
    } catch (error) {
      console.error('Error initializing episodes:', error);
    }
  }

  /**
   * Update episode display order
   */
  static async updateEpisodeOrder(id: string, displayOrder: number): Promise<boolean> {
    try {
      const episodeRef = doc(db, COLLECTION_NAME, id);
      
      await updateDoc(episodeRef, {
        displayOrder,
        updatedAt: new Date()
      });
      
      return true;
    } catch (error) {
      console.error('Error updating episode order:', error);
      return false;
    }
  }
} 